import * as Preferences from '../../constants/USER_PREFERENCE'
import { useAuth0 } from '@auth0/auth0-react'
import { useContext, useState } from 'react'
import { UserContext } from '../../contexts/user-context'
import {
    svc_createUserPreference,
    svc_updateUserPreference
} from '../../services/user.service'
import FormFieldInput from '../ui/FormFieldInput'
import FormFieldSelect from '../ui/FormFieldSelect'
import FormFieldInputRange from '../ui/FormFieldInputRange'
import ButtonSwitch from '../Buttons/ButtonSwitch'

const UserPreferenceControl = ({ userPreference, preferenceKey, onSave }) => {
    const [preferenceHasChanged, setPreferenceHasChanged] = useState(false)
    const [updatedPreference, setUpdatedPreference] = useState({
        [preferenceKey]: userPreference
            ? userPreference[preferenceKey]
            : Preferences.PREF_DEFAULT[preferenceKey]
    })
    const { getAccessTokenSilently } = useAuth0()
    const { ddUser } = useContext(UserContext)

    const handleSave = () => {
        createOrUpdatePreference().then(() => {
            setPreferenceHasChanged(false)
            onSave()
        });
    }

    const createOrUpdatePreference = async () => {
        const accessToken = await getAccessTokenSilently()

        const { data, error } = userPreference
            ? await svc_updateUserPreference(accessToken, ddUser.id, preferenceKey, updatedPreference)
            : await svc_createUserPreference(accessToken, ddUser.id, updatedPreference)

        if (error) {
            console.error("Error creating or updating user preference: ", error)
            return
        }
    }

    return (
        <div className="text-center">
            <ControlElement
                preferenceKey={preferenceKey}
                userPreference={userPreference}
                setPreferenceHasChanged={setPreferenceHasChanged}
                updatedPreference={updatedPreference}
                setUpdatedPreference={setUpdatedPreference}
            />

            {preferenceHasChanged ? (
                <div className="spc-m-t-half">
                    <button className="button button-slim button-green" onClick={handleSave}>
                        Save
                    </button>
                </div>
            ) : null}
        </div>
    )
}

export default UserPreferenceControl

const ControlElement = ({
    preferenceKey,
    userPreference,
    setPreferenceHasChanged,
    updatedPreference,
    setUpdatedPreference
}) => {
    const elType = Preferences.PREF_ELEMENT_TYPE[preferenceKey];
    const isToggle = elType === 'toggle';
    const isRange = elType === 'range';

    const handleRangeChange = (e) => {
        const value = parseInt(e.target.value);
        setUpdatedPreference({
            [preferenceKey]: value
        })

        setPreferenceHasChanged(userPreference
            ? value !== userPreference[preferenceKey]
            : value !== Preferences.PREF_DEFAULT[preferenceKey]
        )
    }

    const handleToggleChange = (e) => {
        const { value } = e.target;
        setUpdatedPreference({
            [preferenceKey]: value ? 1 : 0
        })

        setPreferenceHasChanged(userPreference
            ? value !== userPreference[preferenceKey]
            : value !== Preferences.PREF_DEFAULT[preferenceKey]
        )
    }

    const handleTextChange = (e) => {
        const value = e.target.value;
        setUpdatedPreference({
            [preferenceKey]: value
        })

        setPreferenceHasChanged(userPreference
            ? value !== userPreference[preferenceKey]
            : value !== Preferences.PREF_DEFAULT[preferenceKey]
        )
    }

    if (isToggle) {
        return (
            <ButtonSwitch
                onChange={handleToggleChange}
                isActive={userPreference ? Boolean(userPreference[preferenceKey]) : Preferences.PREF_DEFAULT[preferenceKey]}
            />
        )
    }

    if (isRange) {
        return (
            <FormFieldInputRange
                fieldName={preferenceKey}
                defaultValue={userPreference ? parseInt(userPreference[preferenceKey]) : Preferences.PREF_DEFAULT[preferenceKey]}
                type={Preferences.PREF_ELEMENT_TYPE[preferenceKey]}
                stateObject={updatedPreference}
                updateStateObject={setUpdatedPreference}
                onChange={handleRangeChange}
                formatValue={parseInt}
                additionalProps={{
                    min: 1,
                    max: 10
                }}
            />
        )
    }

    if (elType === 'select') {
        return (
            <FormFieldSelect
                fieldName={preferenceKey}
                label={Preferences.PREF_LABEL[preferenceKey]}
                value={userPreference ? userPreference[preferenceKey] : Preferences.PREF_DEFAULT[preferenceKey]}
                stateObject={updatedPreference}
                updateStateObject={setUpdatedPreference}
                options={Preferences.PREF_OPTIONS[preferenceKey]}
                onChange={handleTextChange}
            />
        )
    }

    return (
        <FormFieldInput
            fieldName={preferenceKey}
            label={Preferences.PREF_LABEL[preferenceKey]}
            value={userPreference ? userPreference[preferenceKey] : Preferences.PREF_DEFAULT[preferenceKey]}
            stateObject={updatedPreference}
            updateStateObject={setUpdatedPreference}
            type={Preferences.PREF_ELEMENT_TYPE[preferenceKey]}
            onChange={handleTextChange}
        />
    )
}
